import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import styled, { css } from "styled-components";

const enum fields {
    variant = "variant",
    label = "label",
    link = "link",
    alignment = "alignment",
    customCSS = "customCSS",
    buttonTextColor = "buttonTextColor",
    buttonBackgroundColor = "buttonBackgroundColor",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.buttonBackgroundColor]: string;
        [fields.buttonTextColor]: string;
        [fields.openInNewWindow]: boolean;
    };
}

export interface ButtonStyles {
    wrapperStyles: InjectableCss;
    buttonCustomStyles: InjectableCss;
}

const wrapperStyles: InjectableCss = {};

export const CmsButton: FC<OwnProps> = ({ fields }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();

    const buttonCustomStyles = {
        css: css`
            background: ${fields.buttonBackgroundColor};
            border-color: ${fields.buttonBackgroundColor};
            color: ${fields.buttonTextColor};
            padding: 0 25.5px !important;
            height: 40px;
            &:hover {
                background: ${fields.buttonBackgroundColor};
                border-color: ${fields.buttonBackgroundColor};
            }

            > span {
                color: ${fields.buttonTextColor};
                font-size: 15px;
            }
        `,
    };
    const onClick = (openInNewWindow: boolean) => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            openInNewWindow ? window.open(link.url, "_blank") : (window.location.href = link.url);
        } else {
            openInNewWindow ? window.open(link.url, "_blank") : history.push(link.url);
        }
    };

    const styles = useMergeStyles("button", { wrapperStyles });

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <ContentWrapper alignment={fields.alignment} {...styles?.wrapperStyles} className="wrapper">
                <Button
                    variant={fields.variant}
                    onClick={() => onClick(fields.openInNewWindow)}
                    className="button"
                    {...buttonCustomStyles}
                >
                    {fields.label}
                </Button>
            </ContentWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: CmsButton,
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.buttonBackgroundColor,
                displayName: "Button Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.buttonTextColor,
                displayName: "Button Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.openInNewWindow,
                fieldType: "General",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
